import {
  AdminTable,
  AdminTableBody,
  AdminTableHeader,
  Button,
  FormError,
  FormSection,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TooltipButton,
} from "@simplicity/ui";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { ScheduleEntry, Schedules, TableConfig } from "../tables/types";
import { ScheduleRoster } from "./ScheduleRoster";
import {
  atShapeHint,
  buildScheduleRows,
  isRunnableCadence,
  RUNNABLE_CADENCES,
  validateAt,
  type ScheduleDef,
  type ScheduleRow,
} from "./scheduleRows";
import { ScheduleRowStatus } from "./ScheduleRowStatus";
import { SchedulesIntro } from "./SchedulesIntro";
import { addTablesTo, removeTableFrom, setTableEntriesIn } from "./scheduleWrites";
import { UnscheduledTablesNotice } from "./UnscheduledTablesNotice";

/**
 * A connector's schedules (SIM-3376). `at` decides when a client syncs (SIM-3356) and used
 * to live on every table entry — 42 copies on one connector, editable only as an untyped
 * free-text row per table. Here it is one field per schedule, validated against the same
 * `parseAtPhase` the resolver uses. Membership is edited here and only here (SIM-3402).
 */

export interface SchedulesPanelProps {
  /** `vars.scheduleDefs` — the definitions. */
  scheduleDefs: Record<string, ScheduleDef>;
  /** `vars.schedules` — table membership. */
  schedules: Schedules;
  /** `vars.tables` — what the picker may offer, and where the sheet reads fields and required params. */
  tables?: TableConfig[];
  /** Omit to render read-only — used where the connector form has no save path. */
  onScheduleDefsChange?: (defs: Record<string, ScheduleDef>) => void;
  /** Omit to render membership read-only, independently of the definitions. */
  onSchedulesChange?: (schedules: Schedules) => void;
  clearBanner?: () => void;
  /**
   * Why saving must be blocked, or null. An invalid `at` is never written through,
   * so without this the form would save the previous value and report success (SIM-3404).
   */
  onInvalidChange?: (reason: string | null) => void;
}

function AtCell({
  row,
  onChange,
  onValidity,
}: {
  row: ScheduleRow;
  onChange: ((at: string | undefined) => void) | undefined;
  onValidity: (name: string, message: string | null) => void;
}) {
  const [text, setText] = useState(row.at ?? "");
  const [error, setError] = useState<string | null>(null);
  // Our own writes are recorded here before they reach `vars`, so re-syncing can tell
  // an external change apart from the keystroke that caused one.
  const synced = useRef(row.at);

  // The cell stays mounted across a save-and-refetch or a version restore, so the
  // displayed text has to follow an `at` that changed underneath it.
  useEffect(() => {
    if (synced.current === row.at) return;
    synced.current = row.at;
    setText(row.at ?? "");
    setError(null);
    onValidity(row.name, null);
  }, [row.at, row.name, onValidity]);

  function handle(value: string) {
    setText(value);
    const message = validateAt(value, row.cadence);
    setError(message);
    onValidity(row.name, message);
    // An invalid value is never written through, so a half-typed `0` cannot
    // reach `vars` and un-anchor the schedule on an autosave.
    if (message) return;
    const trimmed = value.trim();
    synced.current = trimmed === "" ? null : trimmed;
    onChange?.(trimmed === "" ? undefined : trimmed);
  }

  if (!onChange) {
    return row.at === null ? (
      <span className="text-muted-foreground">not set</span>
    ) : (
      <span className="font-mono">{row.at}</span>
    );
  }

  // Sized to its longest real value (`Mon 02:00`), not to a free-text field — the width
  // it gives back is what lets a roster row fit on one line (SIM-3511 / SIM-3515).
  return (
    <div className="w-[7.5rem]">
      <Input
        value={text}
        onChange={(e) => handle(e.target.value)}
        placeholder={atShapeHint(row.cadence)}
        aria-label={`Time ${row.name} runs at`}
        aria-invalid={error ? true : undefined}
        className="font-mono"
      />
      {error && <FormError>{error}</FormError>}
    </div>
  );
}

/** The period a schedule runs on, for a name that is not itself one (SIM-3571). */
function PeriodSelect({
  scheduleName,
  value,
  onChange,
}: {
  scheduleName: string;
  value: string | undefined;
  onChange: (cadence: string) => void;
}) {
  return (
    <Select value={value ?? ""} onValueChange={onChange}>
      <SelectTrigger aria-label={`How often ${scheduleName} runs`} className="h-8 w-28 text-xs">
        <SelectValue placeholder="Runs…" />
      </SelectTrigger>
      <SelectContent>
        {RUNNABLE_CADENCES.map((c) => (
          <SelectItem key={c} value={c}>
            {c}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * A schedule the scheduler has no period for is never due and invisible to every report,
 * so the row says so and offers the fix in place.
 */
function UnrunnableNotice({
  row,
  onCadence,
}: {
  row: ScheduleRow;
  onCadence: ((cadence: string) => void) | undefined;
}) {
  return (
    <div className="mt-1 flex flex-col gap-1">
      <span className="text-xs font-medium text-warning">
        {`Never runs — the scheduler has no "${row.cadence}" period.`}
      </span>
      {onCadence && <PeriodSelect scheduleName={row.name} value={undefined} onChange={onCadence} />}
    </div>
  );
}

function RemoveCell({ row, onRemove }: { row: ScheduleRow; onRemove: () => void }) {
  // Removing a schedule that still has tables would orphan them onto the elapsed rule.
  // `aria-disabled` rather than `disabled` so the control keeps focus: a disabled button
  // is unreachable by keyboard, which would make the reason mouse-only (SIM-3511).
  const blocked = row.tableCount > 0;
  const label = blocked
    ? `Remove the ${row.name} schedule — remove its ${row.tableCount} table${row.tableCount === 1 ? "" : "s"} first`
    : `Remove the ${row.name} schedule`;

  return (
    <TooltipButton
      type="button"
      variant="ghost"
      label={label}
      aria-disabled={blocked || undefined}
      className={blocked ? "opacity-50" : undefined}
      onClick={blocked ? undefined : onRemove}
    >
      <Trash2 className="h-4 w-4" aria-hidden="true" />
    </TooltipButton>
  );
}

function ScheduleTableRow({
  row,
  editable,
  tables,
  onAt,
  onCadence,
  onRemove,
  onValidity,
  onAddTables,
  onRemoveTable,
  onTableEntriesChange,
}: {
  row: ScheduleRow;
  editable: boolean;
  tables: TableConfig[];
  onAt: (at: string | undefined) => void;
  onCadence: (cadence: string) => void;
  onRemove: () => void;
  onValidity: (name: string, message: string | null) => void;
  onAddTables?: (names: string[]) => void;
  onRemoveTable?: (name: string) => void;
  onTableEntriesChange?: (tableName: string, entries: ScheduleEntry[]) => void;
}) {
  return (
    <tr>
      <td className="align-top">
        <span className="font-medium">{row.name}</span>
        {row.renamed && <span className="ml-2 text-xs text-muted-foreground">({row.cadence})</span>}
        {/* Clearing the time silently un-anchors the schedule — the drift SIM-3356
            fixed — so the row says so, not just the field the user just emptied. */}
        {!row.runnable && (
          <UnrunnableNotice row={row} onCadence={editable ? onCadence : undefined} />
        )}
        {row.runnable && row.at === null && (
          <span className="mt-1 block text-xs font-medium text-warning">
            No time set — runs on elapsed time instead of a fixed slot.
          </span>
        )}
        <ScheduleRowStatus schedule={row.name} />
      </td>
      <td className="align-top">
        <AtCell row={row} onChange={editable ? onAt : undefined} onValidity={onValidity} />
      </td>
      <td className="align-top">
        <ScheduleRoster
          row={row}
          tables={tables}
          onAddTables={onAddTables}
          onRemoveTable={onRemoveTable}
          onTableEntriesChange={onTableEntriesChange}
        />
      </td>
      {editable && (
        <td className="align-top">
          <RemoveCell row={row} onRemove={onRemove} />
        </td>
      )}
    </tr>
  );
}

/** The schedules themselves come first; unscheduled tables are called out beneath them. */
function SchedulesTable({
  editable,
  tables,
  schedules,
  children,
}: {
  editable: boolean;
  tables: TableConfig[];
  schedules: Schedules;
  children: ReactNode;
}) {
  return (
    <>
      <AdminTable aria-label="Schedules">
        <AdminTableHeader>
          <tr>
            <th>Schedule</th>
            <th>Runs at</th>
            <th>Tables</th>
            {editable && (
              <th>
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </AdminTableHeader>
        <AdminTableBody>{children}</AdminTableBody>
      </AdminTable>
      <UnscheduledTablesNotice tables={tables} schedules={schedules} />
    </>
  );
}

/**
 * Sits in the section header, matching the Tables section's own add control. A name that is
 * not itself a period needs one chosen, or the schedule is written unrunnable (SIM-3571).
 */
function AddSchedule({
  onAdd,
  taken,
}: {
  onAdd: (name: string, cadence: string | undefined) => void;
  taken: Set<string>;
}) {
  const [name, setName] = useState("");
  const [cadence, setCadence] = useState<string | undefined>(undefined);
  const trimmed = name.trim();
  const duplicate = trimmed !== "" && taken.has(trimmed);
  const needsPeriod = trimmed !== "" && !duplicate && !isRunnableCadence(trimmed);

  return (
    <div className="relative flex items-center gap-2">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="hourly"
        aria-label="New schedule name"
        aria-invalid={duplicate ? true : undefined}
        className="h-8 w-28 text-xs"
      />
      {needsPeriod && <PeriodSelect scheduleName={trimmed} value={cadence} onChange={setCadence} />}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1 text-xs"
        disabled={trimmed === "" || duplicate || (needsPeriod && cadence === undefined)}
        onClick={() => {
          onAdd(trimmed, needsPeriod ? cadence : undefined);
          setName("");
          setCadence(undefined);
        }}
      >
        <Plus className="h-3 w-3" aria-hidden="true" /> Add schedule
      </Button>
      {/* Absolute so a duplicate name cannot change the header's height. */}
      {duplicate && (
        <FormError className="absolute top-full right-0 mt-0.5 whitespace-nowrap">
          {`"${trimmed}" already exists.`}
        </FormError>
      )}
    </div>
  );
}

/** Track which rows hold an unusable time, so the owner of Save can be told. */
function useInvalidRows(rows: ScheduleRow[], onInvalidChange?: (reason: string | null) => void) {
  const [invalid, setInvalid] = useState<Record<string, string>>({});

  const noteValidity = useCallback((name: string, message: string | null) => {
    setInvalid((prev) => {
      if ((prev[name] ?? null) === message) return prev;
      const next = { ...prev };
      if (message) next[name] = message;
      else delete next[name];
      return next;
    });
  }, []);

  // Read through the live rows so a removed schedule's stale entry cannot keep Save disabled.
  const blocking = rows.filter((r) => invalid[r.name] !== undefined).map((r) => r.name);
  const reason =
    blocking.length === 0 ? null : `Fix the time on ${blocking.join(", ")} before saving.`;

  useEffect(() => {
    onInvalidChange?.(reason);
  }, [reason, onInvalidChange]);

  return noteValidity;
}

/** Writers over `vars.scheduleDefs`, each handing the whole next record to `write`. */
function definitionWriters(
  scheduleDefs: Record<string, ScheduleDef>,
  write: (next: Record<string, ScheduleDef>) => void,
) {
  return {
    add: (name: string, cadence: string | undefined) => {
      write({ ...scheduleDefs, [name]: cadence === undefined ? {} : { cadence } });
    },
    setAt: (name: string, at: string | undefined) => {
      const next = { ...scheduleDefs };
      const { at: _dropped, ...rest } = next[name] ?? {};
      const def = at === undefined ? rest : { ...rest, at };
      // An empty definition says nothing the union of defs and buckets does not already say.
      if (Object.keys(def).length === 0) delete next[name];
      else next[name] = def;
      write(next);
    },
    setCadence: (name: string, cadence: string) => {
      write({ ...scheduleDefs, [name]: { ...scheduleDefs[name], cadence } });
    },
    remove: (name: string) => {
      const next = { ...scheduleDefs };
      delete next[name];
      write(next);
    },
  };
}

export function SchedulesPanel({
  scheduleDefs,
  schedules,
  tables = [],
  onScheduleDefsChange,
  onSchedulesChange,
  clearBanner,
  onInvalidChange,
}: SchedulesPanelProps) {
  const rows = buildScheduleRows(scheduleDefs, schedules);
  const editable = onScheduleDefsChange !== undefined;
  const noteValidity = useInvalidRows(rows, onInvalidChange);
  const defs = definitionWriters(scheduleDefs, (next) => {
    onScheduleDefsChange?.(next);
    clearBanner?.();
  });

  function writeSchedules(next: Schedules) {
    onSchedulesChange?.(next);
    clearBanner?.();
  }

  function addTables(name: string, added: string[]) {
    writeSchedules(addTablesTo(schedules, name, added));
  }

  function removeTable(name: string, tableName: string) {
    writeSchedules(removeTableFrom(schedules, name, tableName));
  }

  function setTableEntries(name: string, tableName: string, entries: ScheduleEntry[]) {
    writeSchedules(setTableEntriesIn(schedules, name, tableName, entries));
  }

  if (rows.length === 0 && !editable) return null;

  return (
    <FormSection
      title="Schedules"
      count={rows.length}
      indent={false}
      className="mb-4"
      actions={
        editable ? (
          <AddSchedule taken={new Set(rows.map((r) => r.name))} onAdd={defs.add} />
        ) : undefined
      }
    >
      <SchedulesIntro />
      <SchedulesTable editable={editable} tables={tables} schedules={schedules}>
        {rows.map((row) => (
          <ScheduleTableRow
            key={row.name}
            row={row}
            editable={editable}
            tables={tables}
            onAt={(at) => defs.setAt(row.name, at)}
            onCadence={(cadence) => defs.setCadence(row.name, cadence)}
            onRemove={() => defs.remove(row.name)}
            onValidity={noteValidity}
            onAddTables={onSchedulesChange ? (added) => addTables(row.name, added) : undefined}
            onRemoveTable={onSchedulesChange ? (table) => removeTable(row.name, table) : undefined}
            onTableEntriesChange={
              onSchedulesChange
                ? (table, entries) => setTableEntries(row.name, table, entries)
                : undefined
            }
          />
        ))}
      </SchedulesTable>
    </FormSection>
  );
}
